import { Injectable } from '@angular/core';

import { KameleoonClient, SDKConfigurationType, Environment } from '@kameleoon/javascript-sdk';

import { environment } from '../../environments/environment';
import { Initializable } from './app-init.service';
import { FeatureFlagsProvider, FeatureFlagVariation } from './feature-flags.service';

@Injectable({ providedIn: 'root' })
export class KameleoonFeatureFlagsService implements Initializable, FeatureFlagsProvider {
  private client: KameleoonClient;
  private visitorCode: string | null = null;

  constructor() {
    let kameleoonEnvironment = Environment.Development;
    let kameleoonUpdateInterval = 1;
    if (environment.env === 'production') {
      kameleoonEnvironment = Environment.Production;
      kameleoonUpdateInterval = 60;
    } else if (environment.env === 'staging' || environment.env === 'backstage') {
      kameleoonEnvironment = Environment.Staging;
      kameleoonUpdateInterval = 5;
    }

    const configuration: Partial<SDKConfigurationType> = {
      updateInterval: kameleoonUpdateInterval,
      environment: kameleoonEnvironment,
    };

    this.client = new KameleoonClient({
      siteCode: 'zedskc5jtv',
      configuration: configuration,
    });
  }

  async init(): Promise<void> {
    // - Initialize KameleoonClient
    await this.initClient();
  }

  private async initClient(): Promise<void> {
    try {
      await this.client.initialize();
      this.visitorCode = this.client.getVisitorCode();
      this.client.setLegalConsent(this.visitorCode, true);
    } catch (error) {
      console.error('[Kameleoon] Failed to initialize client:', error);
    }
  }

  getFeatureFlagVariation(name: string): FeatureFlagVariation | null | undefined {
    // Safety: if the service is not initialized, we cannot respond
    if (!this.visitorCode) {
      console.warn(`[Kameleoon] Service not initialized, cannot get variation for "${name}"`);
      return undefined;
    }

    try {
      const variation = this.client.getVariation({
        visitorCode: this.visitorCode,
        featureKey: name,
        track: true,
      });

      // Kameleoon returns an 'off' variation when the feature is disabled
      if (variation.key === 'off') {
        return null; // Explicitly disabled
      }

      // Convert the variables Map to an object
      const data: Record<string, unknown> = {};
      variation.variables.forEach((variable, key) => {
        data[key] = variable.value;
      });

      return {
        name: variation.key,
        data,
      };
    } catch (error) {
      console.log(`[Kameleoon] Error getting variation for "${name}":`, error);
      return undefined; // The provider does not know this flag or an error occurred
    }
  }
}
